/**
 * Admin API Hooks
 *
 * React Query hooks for super admin operations.
 * All endpoints require isSuperAdmin = true in user metadata.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient, useApiClientWithMeta } from '@/lib/api-client';

// ============================================
// Types
// ============================================

export interface AdminDashboardData {
  tenants: { total: number; active: number; disabled: number };
  leads: { total: number; verified: number; thisMonth: number };
  generations: { total: number; thisMonth: number };
  revenue: { payingTenants: number; estimatedMrr: number };
  signupTrend: Array<{ month: string; count: number }>;
}

export interface AdminTenant {
  id: string;
  companyName: string;
  slug: string;
  ownerEmail: string | null;
  subscriptionStatus: string;
  subscriptionTier: string;
  monthlyCredits: number;
  addonCredits: number;
  rolloverCredits: number;
  isDisabled: boolean;
  createdAt: string;
  totalCredits: number;
  leadCount: number;
}

export interface AdminTenantDetail {
  id: string;
  companyName: string;
  slug: string;
  ownerClerkId: string;
  ownerEmail: string | null;
  subscriptionStatus: string;
  subscriptionTier: string;
  trialEndsAt: string | null;
  generationCreditsMonthly: number;
  generationCreditsAddon: number;
  generationCreditsRollover: number;
  creditsCap: number;
  lastCreditResetAt: string | null;
  generationsThisMonth: number;
  webhookUrl: string | null;
  webhookEnabled: boolean;
  emailNotificationsEnabled: boolean;
  isDisabled: boolean;
  createdAt: string;
  updatedAt: string;
  creditBreakdown: {
    monthly: number;
    addon: number;
    rollover: number;
    total: number;
    cap: number;
  };
  leadCount: number;
  generationCount: number;
}

export interface AdminLead {
  id: string;
  clientName: string | null;
  clientEmail: string | null;
  clientPhone: string | null;
  roomType: string | null;
  verificationStatus: string | null;
  callbackRequested: boolean | null;
  createdAt: string;
}

export interface AuditLogEntry {
  id: string;
  action: string;
  details: Record<string, unknown>;
  createdAt: string;
  adminUserEmail: string | null;
  targetTenantName: string | null;
}

// ============================================
// Query Hooks
// ============================================

/**
 * Fetch admin dashboard metrics
 */
export function useAdminDashboard() {
  const api = useApiClient();
  return useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: () => api.get<AdminDashboardData>('/admin/dashboard'),
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Fetch paginated tenant list with filters
 */
export function useAdminTenants(params: {
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  status?: 'enabled' | 'disabled';
  subscription?: string;
  page?: number;
  limit?: number;
}) {
  const api = useApiClientWithMeta();
  const queryString = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '') as [string, string][]
  ).toString();
  return useQuery({
    queryKey: ['admin', 'tenants', params],
    queryFn: () => api.get<AdminTenant>(`/admin/tenants?${queryString}`),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Fetch single tenant detail
 */
export function useAdminTenantDetail(tenantId: string | undefined) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['admin', 'tenant', tenantId],
    queryFn: () => api.get<AdminTenantDetail>(`/admin/tenants/${tenantId}`),
    enabled: !!tenantId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Fetch paginated leads for a specific tenant
 */
export function useAdminTenantLeads(tenantId: string | undefined, params: { page?: number; limit?: number }) {
  const api = useApiClientWithMeta();
  const queryString = new URLSearchParams(
    Object.entries(params)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, String(v)])
  ).toString();
  return useQuery({
    queryKey: ['admin', 'tenant', tenantId, 'leads', params],
    queryFn: () => api.get<AdminLead>(`/admin/tenants/${tenantId}/leads?${queryString}`),
    enabled: !!tenantId,
  });
}

/**
 * Fetch audit log with filters
 */
export function useAdminAuditLog(params: {
  action?: string;
  tenantId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}) {
  const api = useApiClientWithMeta();
  const queryString = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '') as [string, string][]
  ).toString();
  return useQuery({
    queryKey: ['admin', 'auditLog', params],
    queryFn: () => api.get<AuditLogEntry>(`/admin/audit-log?${queryString}`),
  });
}

// ============================================
// Mutation Hooks
// ============================================

/**
 * Grant addon credits to a tenant
 */
export function useGrantCredits() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, amount }: { tenantId: string; amount: number }) =>
      api.post<{ newAddonCredits: number; newTotalCredits: number }>(`/admin/tenants/${tenantId}/credits`, { amount }),
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    },
  });
}

/**
 * Adjust monthly credit allocation for a tenant
 */
export function useAdjustMonthlyCredits() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, monthlyCredits }: { tenantId: string; monthlyCredits: number }) =>
      api.patch<{ newMonthlyCredits: number; newTotalCredits: number }>(`/admin/tenants/${tenantId}/credits`, { monthlyCredits }),
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    },
  });
}

/**
 * Disable a tenant (prevents widget access)
 */
export function useDisableTenant() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) => api.post(`/admin/tenants/${tenantId}/disable`, {}),
    onSuccess: (_, tenantId) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    },
  });
}

/**
 * Enable a previously disabled tenant
 */
export function useEnableTenant() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) => api.post(`/admin/tenants/${tenantId}/enable`, {}),
    onSuccess: (_, tenantId) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    },
  });
}

/**
 * Create a new tenant with owner email invitation
 */
export function useCreateTenant() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { companyName: string; slug: string; ownerEmail: string; initialMonthlyCredits?: number; initialAddonCredits?: number }) =>
      api.post<{ tenant: AdminTenant; inviteLink: string; ownerEmail: string }>('/admin/tenants', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    },
  });
}

/**
 * Edit tenant basic info (name, slug)
 */
export function useEditTenant() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, data }: { tenantId: string; data: { companyName?: string; slug?: string } }) =>
      api.patch(`/admin/tenants/${tenantId}`, data),
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    },
  });
}

/**
 * Send invitation link to tenant owner
 */
export function useSendInvite() {
  const api = useApiClient();
  return useMutation({
    mutationFn: ({ tenantId, email }: { tenantId: string; email?: string }) =>
      api.post<{ inviteLink: string }>(`/admin/tenants/${tenantId}/invite`, email ? { email } : {}),
  });
}
